import { getWeekPeriodFromDate } from "./nutrition-helpers";
import { getFullName } from "./personal-records";

export const TREND_LOOKBACK_WEEKS = 8;

/**
 * Flattens a sets[] array (set-groups with repeated rounds) into an ordered
 * list of outcome objects, one per row, in the same order WeekDayExercise
 * renders its table rows (set-group order, then within-group round order).
 */
export const flattenSetsOutcomes = (sets) => {
  const flat = [];
  (sets || []).forEach((set) => {
    const setCount = Number(set.sets || set.rounds || 1);
    for (let i = 0; i < setCount; i++) {
      flat.push(set.outcomes?.[i]?.outcome ?? null);
    }
  });
  return flat;
};

/**
 * Returns a stable movement key for grouping exercises across sessions.
 * Complex exercises use their complex name when available so all exercises
 * within a complex are tracked together as a single movement.
 */
export const getMovementKey = (exercise) => {
  if (exercise.complexName) return `complex:${exercise.complexName}`;
  return getFullName(exercise);
};

/**
 * Returns the start-of-week key ("YYYY-MM-DD") an exercise falls into, honouring
 * the athlete's configured week start. Uses the same helper Block Review buckets
 * with, so session ordinals line up between the two views. Memoized per date
 * because a single movement group re-visits the same days repeatedly.
 */
const weekKeyFor = (exercise, startingCalendarDay, cache) => {
  const raw = String(exercise.utcDate ?? exercise.date ?? "");
  if (!cache.has(raw)) {
    const { startOfWeek } = getWeekPeriodFromDate(raw, startingCalendarDay);
    cache.set(raw, startOfWeek.format("YYYY-MM-DD"));
  }
  return cache.get(raw);
};

/**
 * Selects the "top" completed set for an exercise in month view (one cluster
 * per exercise). Prefers the set whose completed outcome has the highest load;
 * falls back to the first completed set.
 *
 * Returns { set, setIdx, outcomeIdx } or null if the exercise has no outcomes.
 */
export const getTopCompletedSet = (exercise) => {
  let bestSet = null;
  let bestSetIdx = -1;
  let bestOutcomeIdx = -1;
  let bestLoad = -Infinity;

  exercise.sets?.forEach((set, setIdx) => {
    if (!set.outcomes) return;
    Object.entries(set.outcomes).forEach(([key, entry]) => {
      if (entry.status !== "completed") return;
      const load = entry.outcome?.load ?? -Infinity;
      if (load > bestLoad) {
        bestLoad = load;
        bestSet = set;
        bestSetIdx = setIdx;
        bestOutcomeIdx = Number(key);
      }
    });
    // If nothing beat bestLoad yet but this set has any completed outcome, take
    // the first one as fallback.
    if (bestSet === null) {
      const firstKey = Object.keys(set.outcomes)[0];
      if (
        firstKey !== undefined &&
        set.outcomes[firstKey].status === "completed"
      ) {
        bestSet = set;
        bestSetIdx = setIdx;
        bestOutcomeIdx = Number(firstKey);
      }
    }
  });

  if (bestSet === null) return null;
  return { set: bestSet, setIdx: bestSetIdx, outcomeIdx: bestOutcomeIdx };
};

/**
 * True if the exercise has at least one set with a genuinely "completed"
 * outcome. Drafts (auto-complete sets off, target merely edited) and missed
 * sets don't count - trend/history comparisons must never reference
 * not-yet-finalized data (see buildPreviousOutcomesMap).
 */
const hasCompletedSet = (exercise) =>
  (exercise.sets || []).some((set) =>
    Object.values(set.outcomes || {}).some(
      (entry) => entry?.status === "completed"
    )
  );

/**
 * Builds a synthetic previous-outcomes `sets` array that mirrors the current
 * exercise's group/round structure, filling each flattened row position with
 * the most-recent non-null outcome across the given prior occurrences.
 *
 * Prior occurrences are aligned by ordinal (flattened row order), so a set
 * position with no value in the latest session falls back to the most recent
 * earlier session that logged that position.
 *
 * @param {Object} ex          the current exercise (defines the output shape)
 * @param {Array<Array>} priorFlats  flattened outcome arrays, most-recent first
 * @returns {Array} sets-like array: [{ outcomes: { [i]: { outcome } } }]
 */
const buildMergedPrevSets = (ex, priorFlats) => {
  let pos = 0;
  return (ex.sets || []).map((set) => {
    const setCount = Number(set.sets || set.rounds || 1);
    const outcomes = {};
    for (let i = 0; i < setCount; i++) {
      const p = pos++;
      const found = priorFlats.map((f) => f[p]).find((o) => o != null) ?? null;
      if (found != null) outcomes[i] = { outcome: found };
    }
    // Carry setCount so consumers that re-flatten this (WeekDayExercise via
    // flattenSetsOutcomes) read every round, not just outcomes[0].
    return { sets: setCount, outcomes };
  });
};

/**
 * Builds a map of exercise._id → previous-outcomes sets[] (same shape as an
 * exercise's own `sets`, but only carrying `outcomes[i].outcome`).
 *
 * Algorithm:
 *   1. Combine history + visible exercises; keep only completed ones.
 *   2. Group by movement key, sort each group ascending by utcDate then order.
 *   3. Number each occurrence within its calendar week (session ordinal), the
 *      way Block Review breaks a movement out into "Session 1", "Session 2".
 *   4. For each visible completed exercise, compare against the reference week:
 *      the most recent EARLIER week that logged the movement (never the same
 *      week, so a deload week with no training is skipped over). Match this
 *      session's ordinal there, clamping to the reference week's last session
 *      when it has fewer sessions.
 *
 * So the 2nd squat of this week compares against the 2nd squat of the reference
 * week, rather than against whatever session happens to precede it on the
 * calendar (which for a heavy/light split is the *same* week's other session).
 * When a lift is trained more times this week than in the reference week — the
 * 3rd session of a week whose predecessor had only 2, or a lift ramping from 1x
 * to 2x — the extra session clamps to the reference week's last session rather
 * than reaching back to an older week or comparing within this week. A movement
 * trained twice in its very first week shows no comparison on the 2nd session,
 * since there is no earlier week. Individual set positions the matched session
 * never logged fall back to earlier weeks the same way.
 *
 * @param {Array} visibleExercises   exercises in the currently-viewed window
 * @param {Array} historyExercises   exercises from the lookback window (before
 *                                   the visible start, no overlap expected)
 * @param {number} startingCalendarDay  athlete's week start (0 = Sunday)
 * @returns {Object}  { [exercise._id]: sets[] }
 */
export const buildPreviousOutcomesMap = (
  visibleExercises,
  historyExercises,
  startingCalendarDay = 0
) => {
  const allCompleted = [
    ...(historyExercises || []),
    ...(visibleExercises || []),
  ].filter(hasCompletedSet);

  // Group by movement key
  const byMovement = {};
  for (const ex of allCompleted) {
    const key = getMovementKey(ex);
    if (!byMovement[key]) byMovement[key] = [];
    byMovement[key].push(ex);
  }

  // Sort each group ascending by utcDate, then order within a day
  for (const key of Object.keys(byMovement)) {
    byMovement[key].sort((a, b) => {
      const dateA = Number(a.utcDate) || 0;
      const dateB = Number(b.utcDate) || 0;
      if (dateA !== dateB) return dateA - dateB;
      return (a.order ?? 0) - (b.order ?? 0);
    });
  }

  // Number each occurrence within its calendar week (0-based), so a movement
  // trained more than once a week can be matched slot-for-slot across weeks.
  const weekKeyCache = new Map();
  const weekKeyOf = new Map();
  const sessionIdxOf = new Map();
  for (const key of Object.keys(byMovement)) {
    const countByWeek = {};
    for (const ex of byMovement[key]) {
      const weekKey = weekKeyFor(ex, startingCalendarDay, weekKeyCache);
      const idx = countByWeek[weekKey] ?? 0;
      countByWeek[weekKey] = idx + 1;
      weekKeyOf.set(ex, weekKey);
      sessionIdxOf.set(ex, idx);
    }
  }

  const result = {};

  for (const ex of visibleExercises || []) {
    if (!hasCompletedSet(ex)) continue;
    const key = getMovementKey(ex);
    const group = byMovement[key] || [];
    const myIdx = group.findIndex((g) => String(g._id) === String(ex._id));
    const me = group[myIdx] ?? ex;
    // Gather all prior occurrences, most-recent first, skipping any other
    // instance of this exact document (guards against duplicates in the
    // combined array).
    const priors = [];
    for (let i = myIdx - 1; i >= 0; i--) {
      const candidate = group[i];
      if (String(candidate._id) === String(ex._id)) continue;
      priors.push(candidate);
    }
    // Trend is week-over-week: compare only against an earlier week, never
    // another session in the same week. The reference week is the most recent
    // earlier week that logged this movement (a deload week the lift wasn't
    // trained in contributes no priors, so it's skipped over).
    const myWeek = weekKeyOf.get(me);
    const myN = sessionIdxOf.get(me);
    const crossWeekPriors = priors.filter((p) => weekKeyOf.get(p) !== myWeek);
    if (crossWeekPriors.length) {
      const referenceWeek = weekKeyOf.get(crossWeekPriors[0]);
      // Match this session's ordinal in the reference week; when that week has
      // fewer sessions, clamp to its last (the largest ordinal <= N). Session
      // numbering is dense per week, so this is an exact match when available.
      const target = crossWeekPriors
        .filter(
          (p) =>
            weekKeyOf.get(p) === referenceWeek && sessionIdxOf.get(p) <= myN
        )
        .sort((a, b) => sessionIdxOf.get(b) - sessionIdxOf.get(a))[0];
      // Target leads; the remaining cross-week priors stay most-recent-first so
      // a set row the target session never logged falls back to an earlier week
      // (still never the same week).
      const ordered = target
        ? [target, ...crossWeekPriors.filter((p) => p !== target)]
        : crossWeekPriors;
      const priorFlats = ordered.map((p) => flattenSetsOutcomes(p.sets));
      result[String(ex._id)] = buildMergedPrevSets(ex, priorFlats);
    }
  }

  return result;
};
